import { staticFile } from 'remotion';
import { loadFont } from '@remotion/fonts';
import * as Montserrat from '@remotion/google-fonts/Montserrat';
import * as Bangers from '@remotion/google-fonts/Bangers';
import * as ComicNeue from '@remotion/google-fonts/ComicNeue';
import * as Inter from '@remotion/google-fonts/Inter';
import * as Lato from '@remotion/google-fonts/Lato';
import * as Merriweather from '@remotion/google-fonts/Merriweather';
import * as Mulish from '@remotion/google-fonts/Mulish';
import * as OpenSans from '@remotion/google-fonts/OpenSans';
import * as Oswald from '@remotion/google-fonts/Oswald';
import * as Poppins from '@remotion/google-fonts/Poppins';
import * as Roboto from '@remotion/google-fonts/Roboto';
import * as SourceCodePro from '@remotion/google-fonts/SourceCodePro';
import * as SourceSans3 from '@remotion/google-fonts/SourceSans3';
import * as VarelaRound from '@remotion/google-fonts/VarelaRound';

// Custom font configurations
const customFonts = [
  {
    family: 'Konga next',
    url: staticFile('fonts/Konga Next.otf'),
  },
  {
    family: 'THE BOLD FONT',
    url: staticFile('fonts/THE BOLD FONT.otf'),
  },
  {
    family: 'Khmer Muol',
    url: staticFile('fonts/userFonts/KhmerMuol.ttf'),
  },
  {
    family: 'Khmer OS',
    url: staticFile('fonts/userFonts/KhmerOS.ttf'),
  },
  {
    family: 'Khmer OS Apsra',
    url: staticFile('fonts/userFonts/KhmerOSApsra.ttf'),
  },
  {
    family: 'Khmer OS Battambang',
    url: staticFile('fonts/userFonts/KhmerOSbattambang.ttf'),
  },
  {
    family: 'Khmer OS Content',
    url: staticFile('fonts/userFonts/KhmerOScontent.ttf'),
  },
];

// Google Fonts array for easier management
const googleFonts = [
  Montserrat,
  Bangers,
  ComicNeue,
  Inter,
  Lato,
  Merriweather,
  Mulish,
  OpenSans,
  Oswald,
  Poppins,
  Roboto,
  SourceCodePro,
  SourceSans3,
  VarelaRound,
];

/**
 * Load a single custom font
 */
export const loadCustomFont = async (fontConfig: { family: string; url: string }) => {
  try {
    await loadFont(fontConfig);
    console.log(`Font loaded: ${fontConfig.family}`);
  } catch (error) {
    console.error(`Failed to load font ${fontConfig.family}:`, error);
  }
};

/**
 * Load all custom fonts
 */
export const loadAllCustomFonts = async () => {
  const fontPromises = customFonts.map(loadCustomFont);
  await Promise.all(fontPromises);
  console.log('All custom fonts loaded!');
};

/**
 * Load a single Google font
 */
export const loadGoogleFont = (font: any) => {
  try {
    font.loadFont();
    console.log(`Google font loaded`);
  } catch (error) {
    console.error(`Failed to load Google font:`, error);
  }
};

/**
 * Load all Google fonts
 */
export const loadAllGoogleFonts = () => {
  googleFonts.forEach(loadGoogleFont);
  console.log('All Google fonts loaded!');
};

/**
 * Load all fonts (both custom and Google fonts)
 */
export const loadAllFonts = async () => {
  try {
    // Load custom fonts asynchronously
    await loadAllCustomFonts();
    
    // Load Google fonts
    loadAllGoogleFonts();
    
    console.log('All fonts loaded successfully!');
  } catch (error) {
    console.error('Error loading fonts:', error);
  }
};

/**
 * Load specific fonts by name
 */
export const loadSpecificFonts = async (fontNames: string[]) => {
  const customFontsToLoad = customFonts.filter(font => 
    fontNames.includes(font.family)
  );
  
  // For Google fonts, we need to match against known font names
  const googleFontNames = [
    'Montserrat', 'Bangers', 'Comic Neue', 'Inter', 'Lato', 
    'Merriweather', 'Mulish', 'Open Sans', 'Oswald', 'Poppins',
    'Roboto', 'Source Code Pro', 'Source Sans 3', 'Varela Round'
  ];
  
  const googleFontsToLoad = googleFonts.filter((_, index) => 
    fontNames.some(name => googleFontNames[index] === name)
  );
  
  // Load custom fonts
  if (customFontsToLoad.length > 0) {
    const customPromises = customFontsToLoad.map(loadCustomFont);
    await Promise.all(customPromises);
  }
  
  // Load Google fonts
  if (googleFontsToLoad.length > 0) {
    googleFontsToLoad.forEach(loadGoogleFont);
  }
  
  console.log(`Loaded ${fontNames.length} specific fonts`);
};

// Export font lists for reference
export { customFonts, googleFonts }; 